import React from 'react';
import { useLocation } from 'react-router-dom';
import AimUpNavigation from '@/components/redesign/AimUpNavigation';
import { NoIndex } from '@/components/seo/NoIndex';
import { TrialWelcomeDialog } from '@/components/TrialWelcomeDialog';
import { UsageSurveyDialog } from '@/components/UsageSurveyDialog';
import { BoardManagerAnnouncement } from '@/components/BoardManagerAnnouncement';
import { FeedbackWidget } from '@/components/FeedbackWidget';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import '@/styles/aimup-redesign-common.css';
import '@/styles/aimup-redesign-screens.css';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const { user, loading } = useAuth();
  // Die Ankündigung erhält Vorrang vor bestehenden Dashboard-Dialogen.
  const [announcementVisible, setAnnouncementVisible] = React.useState<boolean | null>(null);
  // Alle authentifizierten Dashboard-Routen benutzen dieselbe Navigation.
  // Standalone-Spieloberflächen und Admin-Routen bleiben bewusst außerhalb.
  const { pathname } = useLocation();
  const viewName = pathname.replace(/^\/(?:de|en)(?=\/|$)/, '').split('/').filter(Boolean)[0] || 'startseite';
  const isRedesignDashboard = /^\/(?:de\/|en\/)?startseite\/?$/.test(pathname);
  // Der Performance-Test und der Trainingsplan (Vorlage training_plan_*) haben einen eigenen Fokus-Kopf ohne App-Navigation.
  const isStandalonePerformanceTest = /^\/(?:de\/|en\/)?(?:analyse|training-planen|mein-wochenplan)\/?$/.test(pathname);

  // While Supabase is still restoring the session, show a spinner instead of
  // flashing the "logged out" state (which would hide the profile menu and
  // unmount the header on every navigation).
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center w-full bg-background">
        <NoIndex />
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex flex-col w-full bg-background${user && !isStandalonePerformanceTest ? " aur-layout" : ""}${isRedesignDashboard ? " aur-dashboard-route" : ""}${user && !isStandalonePerformanceTest ? ` aur-view-${viewName}` : ""}`}>
      {/* All dashboard/authenticated pages must not be indexed by search engines. */}
      <NoIndex />
      {user && !isStandalonePerformanceTest && <AimUpNavigation />}
      {user && <BoardManagerAnnouncement key={user.id} userId={user.id} onVisibilityChange={setAnnouncementVisible} />}
      {user && announcementVisible === false && <TrialWelcomeDialog />}
      {user && announcementVisible === false && <UsageSurveyDialog />}
      {user && <FeedbackWidget />}
      <main className="flex flex-1 flex-col">
        {children}
      </main>
    </div>
  );
};

